import Typography from "../../commons/Typography/Typography";
import "../../css/ScrollbarStyles.css";
import useSubUsers, { IFilters } from "../../state/hooks/users/useSubUsers";
import { Fragment, useEffect, useState } from "react";
import { FiltersComponent } from "../../commons/FiltersComponent/FiltersComponent";
import FormatDate from "../../utils/helpers/formatDate";
import { useNavigate, useLocation } from "react-router";

const filterOptions = [
  { value: "firstName", label: "First Name" },
  { value: "lastName", label: "Last Name" },
  { value: "search", label: "Search" },
  { value: "id", label: "User ID" },
  { value: "accountType", label: "Account Type" },
  { value: "countryCode", label: "Country Code" },
];

interface UserData {
  id: string;
  createdAt: string;
  premium: string;
  countryCode: string;
  accountType: string;
  firstName: string;
  lastName: string;
  package?: string;
}

interface PageData {
  data: UserData[];
  meta: {
    total: number;
    page: number;
    limit: number;
    totalPage: number;
  };
}

const SubUsers = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [filtersCount, setFiltersCount] = useState<IFilters>({});
  const [activeFilters, setActiveFilters] = useState<IFilters>({});
  const ITEMS_PER_PAGE = 10;

  const {
    data,
    isLoading,
    isError,
    error,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useSubUsers(true, { ...activeFilters, limit: ITEMS_PER_PAGE });

  useEffect(() => {
    // Get userId from query parameter if it exists
    const queryParams = new URLSearchParams(location.search);
    const subUserId = queryParams.get("subUserId");

    if (subUserId) {
      // Set initial filter to subUserId with the value from the URL
      const initialFilters: IFilters = {
        id: subUserId,
      };

      setFiltersCount(initialFilters);
      setActiveFilters(initialFilters);
    } else {
      // Default behavior - add empty filter
      addFilter();
    }
  }, [location.search]);

  const addFilter = () => {
    const newFilter = filterOptions.find(
      (option) => !Object.keys(filtersCount).includes(option.value)
    );
    if (newFilter) {
      setFiltersCount((prevFilters) => ({
        ...prevFilters,
        [newFilter.value]: "",
      }));
    }
  };

  const handleScroll = (e: any) => {
    const bottom =
      e.target.scrollHeight - e.target.scrollTop <= e.target.clientHeight + 100;

    if (bottom) {
      if (isFetchingNextPage && hasNextPage) return;
      fetchNextPage();
    }
  };

  const handleSearch = () => {
    setActiveFilters(filtersCount);
  };

  const DEBOUNCE_MS = 500;
  useEffect(() => {
    const hasAnyFilterValue = Object.values(filtersCount).some(
      (value) => value !== ""
    );
    if (Object.keys(filtersCount).length === 0 || !hasAnyFilterValue) return;

    const timeoutId = window.setTimeout(() => {
      setActiveFilters(filtersCount);
    }, DEBOUNCE_MS);

    return () => clearTimeout(timeoutId);
  }, [filtersCount]);

  const hasData = data && data.pages && data.pages.length > 0;

  return (
    <div className="flex px-30 pt-55 pb-20 flex-col bg-light-grey-1 h-full w-full">
      <h2 className="text-dark-grey font-bold text-2xl mb-30">Sub users</h2>
      <FiltersComponent
        setFiltersCount={setFiltersCount}
        filterOptions={filterOptions}
        filtersCount={filtersCount}
        onSearch={handleSearch}
      />
      <div
        className="overflow-x-auto rounded-xl border-light-grey-3 border-solid border"
        onScroll={handleScroll}
      >
        {isLoading ? (
          <div className="p-20">Loading...</div>
        ) : isError ? (
          <div className="p-20">
            Error:{" "}
            {error instanceof Error ? error.message : "Something went wrong"}
          </div>
        ) : !hasData ? (
          <div className="p-20">No data available</div>
        ) : (
          <table className="table-auto w-full overflow-x-auto bg-white rounded-xl border-collapse text-dark-grey">
            <thead className="sticky top-0">
              <tr className=" bg-gray-100 h-48 bg-light-grey-4">
                <th className="pl-20 text-left">
                  <Typography weight="extrabold">ID</Typography>
                </th>
                <th className="pl-20 text-left">
                  <Typography weight="extrabold">First name</Typography>
                </th>
                <th className="pl-20 text-left">
                  <Typography weight="extrabold">Last name</Typography>
                </th>
                <th className="pl-20 text-left">
                  <Typography weight="extrabold">Package</Typography>
                </th>
                <th className="pl-20 text-left">
                  <Typography weight="extrabold">Account type</Typography>
                </th>
                <th className="pl-20 text-left">
                  <Typography weight="extrabold">Premium</Typography>
                </th>
                <th className="pl-20 text-left">
                  <Typography weight="extrabold">Country</Typography>
                </th>
                <th className="pl-20 text-left">
                  <Typography weight="extrabold">Creation Date</Typography>
                </th>
              </tr>
            </thead>
            <tbody>
              {data.pages.map((page: any, pageIndex: number) => {
                if (!page || !page.data) return null;

                return (
                  <Fragment key={page.meta?.page || `page-${pageIndex}`}>
                    {page.data.map((user: UserData, userIndex: number) => (
                      <tr
                        className="cursor-pointer border-light-grey-3 border-solid border-t hover:bg-light-grey-1"
                        key={user.id || `user-${userIndex}`}
                        onClick={(e) => {
                           const url = `/sub-users/${user.id}`;
                          if (e.metaKey || e.ctrlKey) {
                           
                          window.open(url, "_blank");
                          } else {
                            navigate(url);
                          }
                          
                        }}
                      >
                        <td className="px-20 py-20">
                          <Typography weight="semibold">{user.id}</Typography>
                        </td>
                        <td className="px-20 py-20">
                          <Typography weight="semibold">
                            {user.firstName}
                          </Typography>
                        </td>
                        <td className="px-20 py-20">
                          <Typography weight="semibold">
                            {user.lastName}
                          </Typography>
                        </td>
                        <td className="px-20 py-20">
                          <Typography weight="semibold">
                            {user.package || "N/A"}
                          </Typography>
                        </td>
                        <td className="px-20 py-20">
                          <Typography weight="semibold">
                            {user.accountType}
                          </Typography>
                        </td>
                        <td className="px-20 py-20">
                          <Typography weight="semibold">
                            {user.premium === "true" ? "Yes" : "No"}
                          </Typography>
                        </td>
                        <td className="px-20 py-20">
                          <Typography weight="semibold">
                            {user.countryCode}
                          </Typography>
                        </td>
                        <td className="px-20 py-20">
                          <Typography weight="semibold">
                            {FormatDate(user.createdAt) || ""}
                          </Typography>
                        </td>
                      </tr>
                    ))}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default SubUsers;
